import { TILE_INNER_PADDING, TILE_SIZE } from '@/config/constants';
import type { DotPosition } from '@/types/models';

export interface DotLayoutOptions {
  count: number;
  tileSize?: number;
  padding?: number;
  dotRadius?: number;
}

const dist2 = (a: DotPosition, b: DotPosition) => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
};

export function generateDotLayout({
  count,
  tileSize = TILE_SIZE,
  padding = TILE_INNER_PADDING,
  dotRadius
}: DotLayoutOptions): DotPosition[] {
  const safeCount = Math.max(0, Math.floor(count));
  if (safeCount === 0) return [];

  const usable = tileSize - padding * 2;
  const r = dotRadius ?? Math.max(8, Math.min(18, Math.floor(48 - safeCount * 1.6)));
  const minDist = r * 2 + 4;
  const minDist2 = minDist * minDist;

  const positions: DotPosition[] = [];
  const maxAttempts = 60;

  for (let i = 0; i < safeCount; i++) {
    let placed = false;
    for (let a = 0; a < maxAttempts; a++) {
      const x = padding + r + Math.random() * (usable - r * 2);
      const y = padding + r + Math.random() * (usable - r * 2);
      const candidate = { x, y };
      let collides = false;
      for (const p of positions) {
        if (dist2(candidate, p) < minDist2) {
          collides = true;
          break;
        }
      }
      if (!collides) {
        positions.push(candidate);
        placed = true;
        break;
      }
    }
    if (!placed) {
      positions.push(fallbackGridPosition(i, safeCount, tileSize, padding, r));
    }
  }

  return positions;
}

function fallbackGridPosition(
  index: number,
  count: number,
  tileSize: number,
  padding: number,
  r: number
): DotPosition {
  const cols = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / cols);
  const usable = tileSize - padding * 2 - r * 2;
  const cx = index % cols;
  const cy = Math.floor(index / cols);
  return {
    x: padding + r + (cols === 1 ? usable / 2 : (cx * usable) / (cols - 1)),
    y: padding + r + (rows === 1 ? usable / 2 : (cy * usable) / (rows - 1))
  };
}

export function dotRadiusFor(count: number): number {
  return Math.max(8, Math.min(18, Math.floor(48 - count * 1.6)));
}
